import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePasswordConstraints } from '../../../hooks/use-password-constraints';
import { Button } from '../../button/button';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { useUpdatePasswordQuery } from '../hooks/use-update-password-query';
import { GoToLoginPageButton } from './go-to-login-page-button';
import { Success } from './success';

export type FormValues = {
  password: string;
};

const Form = () => {
  const { t } = useTranslation();
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      password: '',
    },
  });

  const { isValid: isPasswordValid, render: renderPasswordConstraints } = usePasswordConstraints({
    password: watch('password'),
  });

  const {
    buildErrorList,
    excludeErrorPaths,
    getErrorForPath,
    hasError,
    mutation: resetPasswordMutation,
  } = useUpdatePasswordQuery();

  React.useEffect(() => {
    setError('password', {
      type: 'custom',
      message: getErrorForPath('body.password'),
    });
  }, [getErrorForPath, setError]);

  const excludeDefaultErrors = excludeErrorPaths(['body.password']);

  if (resetPasswordMutation.isSuccess) {
    return <Success />;
  }

  return (
    <form onSubmit={handleSubmit((form) => resetPasswordMutation.mutate(form))}>
      <Stack spacing={1}>
        <LabelledContainer
          required={true}
          text={t('passwordResetForm.form.password', { ns: 'components' })}
        >
          {(formElementId) => (
            <>
              <TextBox
                {...register('password', {
                  required: true,
                  validate: () => isPasswordValid,
                })}
                autoFocus={true}
                disabled={resetPasswordMutation.isPending || resetPasswordMutation.isSuccess}
                errorMessage={errors.password?.message}
                id={formElementId()}
                type="password"
              />

              {renderPasswordConstraints()}
            </>
          )}
        </LabelledContainer>

        {hasError(excludeDefaultErrors) &&
          buildErrorList({
            filter: excludeDefaultErrors,
          })}

        <Stack direction="row" justifyContent={'space-between'}>
          <GoToLoginPageButton
            color="secondary"
            disabled={resetPasswordMutation.isPending}
            variant="text"
          />

          <Button
            color="primary"
            loading={resetPasswordMutation.isPending}
            type="submit"
            variant="contained"
          >
            {t('save', { ns: 'common' })}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

export { Form };
